import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { Provider } from '@angular/core';

import { stripQueryString } from '../utils/dot-cms-image-source';

/**
 * CUSTOMIZATION: `IMAGE_LOADER` for dotCMS assets, emitting
 * `/dA/<id>/<field>/<name>/{width}w/{quality}q`.
 *
 * `provideDotCMSImageLoader` from `@dotcms/angular` is not usable here: it prepends `/dA/` to
 * any path lacking it, and the only live registration point (`DotCmsUveRoutingModule`) is
 * imported eagerly, so it would rewrite every ICM and `/assets/` image app-wide. It also takes
 * a static host, while the dotCMS host is runtime configuration.
 *
 * The quality segment is what triggers WebP and the real compression, so it is always emitted;
 * width alone returns the original format. Transform segments appended after a query string are
 * silently ignored, hence `stripQueryString` and the re-added `language_id`.
 */

/** dotCMS assets are always served from a `/dA/` path; everything else is left untouched. */
function isDotCmsAsset(src: string): boolean {
  return src?.includes('/dA/');
}

export interface DotCmsImageLoaderParams {
  /** Compression quality, 1–100. Drives both WebP conversion and file size. */
  quality?: number;
  /** dotCMS language id; defaults to 1. */
  languageId?: string | number;
  /** UVE editor cache-buster, re-appended after the transform segments. */
  publish?: string;
}

export function buildDotCmsImageUrl(config: ImageLoaderConfig): string {
  const params = (config.loaderParams ?? {}) as DotCmsImageLoaderParams;

  // Non-dotCMS origins (ICM `effectiveUrl`, local `/assets/`) pass through untouched.
  if (!isDotCmsAsset(config.src)) {
    return config.src;
  }

  const path = stripQueryString(config.src);
  const quality = params.quality ?? 75;
  const languageId = params.languageId ?? '1';

  // Width is absent for the `ngSrcset`-less base `src`; still emit quality so that request is
  // WebP too, rather than falling back to the multi-megabyte original.
  const transform = config.width ? `/${config.width}w/${quality}q` : `/${quality}q`;
  const query = params.publish ? `?language_id=${languageId}&publish=${params.publish}` : `?language_id=${languageId}`;

  return `${path}${transform}${query}`;
}

/**
 * Registers the loader in the root injector. Safe app-wide because non-`/dA/` sources are
 * returned unchanged.
 */
export function provideDotCmsImageLoader(): Provider[] {
  return [{ provide: IMAGE_LOADER, useValue: buildDotCmsImageUrl }];
}
